import React from 'react'
import { ConsumerApplicationIntegration, EnvelopeKind, errorToString, InstallerRegistrationPayload, StatusData, TEST_APP_ID, HELP_CENTER_LINK } from '@oneethos/shared'
import { toast } from 'react-toastify'
import { GrTest } from 'react-icons/gr'
import { FiSend, FiHelpCircle, FiUploadCloud, FiExternalLink } from 'react-icons/fi'
import { MdOutlineChangeCircle, MdSync } from 'react-icons/md'
import api from '../api-client'
import { useAppState } from '../hooks'
import { useState } from 'react'
import { Spinner } from '@fluentui/react'
import ContractIssueResolverModal from './contract-issue-resolver-modal'
import { Modal } from './modal'
import { AdditionalDocumentUpload } from './documents'

type ProjectActionsProps = {
  project: StatusData
  installer: InstallerRegistrationPayload
  onChange: (project: StatusData) => void
}

type ActionButtonProps = {
  isLoading?: boolean,
  handleClick: () => void,
  label: string,
  icon: React.ReactNode,
  isTestEnvironment?: boolean
}

const ActionButton = ({ isLoading, handleClick, label, icon, isTestEnvironment = false }: ActionButtonProps) => {
  return (
    <div className="col">
      {isLoading ? (
        <Spinner />
      ) : (
        <button 
          type="button" 
          className={`btn btn-sm btn-outline-primary w-100 ${isTestEnvironment ? 'btn-outline-secondary' : 'btn-outline-primary'}`} 
          onClick={handleClick}>
          {icon}{label}
        </button>
      )}
    </div>
  )
}

export const ProjectActions = ({
  project,
  installer,
  onChange,
}: ProjectActionsProps) => {
  const { tenant } = useAppState()
  const backend = tenant.config.consumerApplicationIntegration
  const [isSyncing, setIsSyncing] = useState(false)
  const [isResendingReferral, setIsResendingReferral] = useState(false)
  const [isSendingNewReferral, setIsSendingNewReferral] = useState(false)
  const [showContractModal, setShowContractModal] = useState(false)
  const [showUploadDoc, setShowUploadDoc] = useState(false)

  const handleCloseUploadDocModal = () => setShowUploadDoc(false)
  const handleOpenUploadDocModal = () => setShowUploadDoc(true)

  const handleResendReferral = () => {
    setIsResendingReferral(true)
    api.post(`/loanapps/${project.id}/resend-referral`)
      .then(() => toast.success('Referral email sent'))
      .catch(ex => toast.error(errorToString(ex)))
      .finally(() => setIsResendingReferral(false))
  }

  const handleSendNewReferral = () => {
    setIsSendingNewReferral(true)
    api.post(`/loanapps/${project.id}/send-new-referral`)
      .then(() => toast.success('New referral link sent'))
      .catch(ex => toast.error(errorToString(ex)))
      .finally(() => setIsSendingNewReferral(false))
  }

  const handleSendLoanDocs = () => {
    api.post(`/loanapps/${project.id}/envelopes`, { send: true })
      .then(() => toast.success('Loan docs sent'))
      .catch(ex => toast.error(errorToString(ex)))
  }

  const handleSendLoanModDocs = () => {
    api.post(`/loanapps/${project.id}/envelopes`, { send: true, kind: EnvelopeKind.LoanModification })
      .then(() => toast.success('Loan modification docs sent'))
      .catch(ex => toast.error(errorToString(ex)))
  }

  const handleSendTestNotification = () => {
    api.post(`/loanapps/${project.id}/test-notification`)
      .then(() => toast.success('Test notification sent'))
      .catch(ex => toast.error(errorToString(ex)))
  }

  const handleSyncFromAirtable = () => {
    setIsSyncing(true)
    api.get(`/loanapps/${project.lookup}/sync`)
      .then(res => {
        toast.success('Synced from Airtable')
        onChange(new StatusData(res))
      })
      .catch(ex => toast.error(ex.error || ex.message, { autoClose: false }))
      .finally(() => setIsSyncing(false))
  }

  return (
    <div className="project-actions row row-cols-3 g-2 py-3">
      {project.installersStatus === 'Awaiting Application' && (
        <ActionButton
          isLoading={isResendingReferral}
          handleClick={handleResendReferral}
          label="Resend Referral Email"
          icon={<FiSend className="me-2" />} />
      )}
      {project.applicationReceived && !project.isClosed && project.nTPGiven && (
        <ActionButton
          handleClick={handleOpenUploadDocModal}
          label="Upload Other Documents"
          icon={<FiUploadCloud className="me-2" />} />
      )}
      {showUploadDoc && (
        <Modal onClose={handleCloseUploadDocModal} hideCloseButton={true}>
          <AdditionalDocumentUpload
            project={project}
            uploadType='other'
            onChange={onChange}
          />
        </Modal>
      )}
      {(project.isPrequalified === 'yes' || project.nTPGiven) && (
        <ActionButton
          handleClick={() => setShowContractModal(true)}
          label="Submit Change Order"
          icon={<MdOutlineChangeCircle className="me-2" />}
        />
      )}
      {showContractModal && (
        <ContractIssueResolverModal
          project={project}
          onClose={() => setShowContractModal(false)}
          onChange={onChange}
        />
      )}
      <ActionButton
        handleClick={() => window.open(HELP_CENTER_LINK, '_blank')}
        label="I Need Help"
        icon={<FiHelpCircle className="me-2" />}
      />
      {installer.isSupport && (
        <>
          <ActionButton
            isLoading={isSendingNewReferral}
            handleClick={handleSendNewReferral}
            label="Send New Referral Link"
            icon={<FiSend className="me-2" />}
          />
          {project.projectType === 'residential' && (
            <ActionButton
              handleClick={() => window.open(`/apply/${project.lookup}`, '_blank')}
              label="View Application"
              icon={<FiExternalLink className="me-2" />}
            />
          )}
          {project.appId && project.appId !== TEST_APP_ID && backend === ConsumerApplicationIntegration.DecisionPro && (
            <ActionButton
              isLoading={isSyncing}
              handleClick={handleSyncFromAirtable}
              label="Sync From Airtable"
              icon={<MdSync className="me-2" />}
            />
          )}
          {process.env.REACT_APP_NODE_ENV === 'test' && (
            <>
              <ActionButton
                isTestEnvironment={true}
                handleClick={handleSendLoanDocs}
                label="Send Loan Docs"
                icon={<GrTest title="Test Only" className="me-2" />}
              />
              <ActionButton
                isTestEnvironment={true}
                handleClick={handleSendLoanModDocs}
                label="Send Loan Modification Docs"
                icon={<GrTest title="Test Only" className="me-2" />}
              />
              <ActionButton
                isTestEnvironment={true}
                handleClick={handleSendTestNotification}
                label="Send Test Notification"
                icon={<GrTest title="Test Only" className="me-2" />}
              />
            </>
          )}
        </>
      )}
    </div>
  )
}

export default ProjectActions
